/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import {
  getPrimaryLanguage,
  IConfigurationModel,
  RouteHelper,
} from "@nf/common";
import React from "react";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import { useTranslation } from "next-i18next";
import {
  fallbackPageMetadata,
  PageMetadataKeys,
  pageMetaDataTranslationKeys,
} from "./pageMetadata";
import { CountryCode, VALID_LANGUAGE } from "@nf/constants";
import StructuredData from "./StructuredData";
import getOrganizationSchema from "schema/getOrganizationSchema";
import { useCountry } from "hooks";

interface ISeoProps {
  screenKey: PageMetadataKeys;
  configuration?: IConfigurationModel;
}

export const SEO = ({ screenKey, configuration }: ISeoProps) => {
  const { t } = useTranslation("translations");
  const router = useRouter();
  const country = useCountry();

  const baseUrl = process.env.NEXT_PUBLIC_MAIN_DOMAIN ?? "";
  const href = baseUrl + router.asPath;

  const isHomePage = configuration?.Routes?.HOME.includes(screenKey);
  const canonicalUrl = isHomePage
    ? RouteHelper.getMainCanonicalUrl(country)
    : RouteHelper.getCountryCanonicalUrl(router.pathname, router.query);

  const title = t(
    pageMetaDataTranslationKeys?.[screenKey]?.title,
    fallbackPageMetadata.default.title
  );
  const description = t(
    pageMetaDataTranslationKeys?.[screenKey]?.description,
    fallbackPageMetadata.default.description
  );

  const languages =
    VALID_LANGUAGE[country.toLowerCase() as Lowercase<CountryCode>] ?? [];

  const hreflangAlternates = [
    ...languages.map((lang) => ({
      hrefLang: `${lang}-${country.toUpperCase()}`,
      href: isHomePage
        ? `${baseUrl}/${country}/${lang}`
        : `${baseUrl}${RouteHelper.replacePathSegments(router.pathname, {
            ...router.query,
            country,
            language: lang,
          })}`,
    })),
    {
      hrefLang: "x-default",
      href: isHomePage
        ? `${baseUrl}/${country}/${getPrimaryLanguage(country)}`
        : `${baseUrl}${RouteHelper.replacePathSegments(router.pathname, {
            ...router.query,
            country,
            language: getPrimaryLanguage(country),
          })}`,
    },
  ];

  return (
    <>
      {isHomePage && <StructuredData schemas={[getOrganizationSchema()]} />}
      <NextSeo
        title={title}
        description={description}
        canonical={canonicalUrl}
        languageAlternates={hreflangAlternates}
        openGraph={{
          title: title,
          description,
          url: href,
          site_name: process.env.NEXT_PUBLIC_NAME,
        }}
      />
    </>
  );
};

export default SEO;
